import { Link } from 'react-router-dom';
import { useFetch } from '@/hooks/useFetch';
import { useAuth } from '@/layouts/RootLayout';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ErrorState, Metric, PageHeader, Panel } from '@/components/kit';
import ReferralLinkBox from '@/components/account/ReferralLinkBox';
import ReferralShareCard from '@/components/account/ReferralShareCard';
import { findMyCustomer } from '@/shared/account';
import { loadMyReferral } from '@/shared/referralLink';
import { GIFT_MONTHS_PER_REFERRAL, MIN_REFERRAL_MONTHS } from '@/shared/referrals';

export const route = { path: '/account/referrals', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'Gift', label: 'Refer a friend', section: 'My account', profiles: ['Customer_c'], order: 15 };

const STEPS = [
  { icon: 'Share2', title: 'Share your link or picture', text: 'Send it in a chat, post it to your story, or just tell friends your username.' },
  {
    icon: 'ShoppingCart',
    title: `They buy ${MIN_REFERRAL_MONTHS} months or longer`,
    text: 'Your username fills in at checkout when they come through your link. They can also type it.',
  },
  {
    icon: 'Gift',
    title: `You get ${GIFT_MONTHS_PER_REFERRAL} month free`,
    text: 'Once their line is live, a free month of your service is added to your line.',
  },
];

export default function Referrals() {
  const { user } = useAuth();
  const ref = useFetch(loadMyReferral, []);
  const me = useFetch(() => findMyCustomer(user), [user?.emailAddress]);
  const earned = Number(me.data?.referrals_earned_c || 0);
  const r = ref.data;

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="My account"
        title="Refer a friend"
        description={`Your link and share picture. Every friend who buys ${MIN_REFERRAL_MONTHS}+ months through it earns you a free month.`}
      />

      {ref.loading && r === undefined ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <Skeleton className="aspect-[4/5] w-full max-w-md" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : ref.error ? (
        <ErrorState message={ref.error} onRetry={ref.run} />
      ) : !r?.username ? (
        <Panel title="Your link is on its way" icon="Hourglass" bodyClassName="py-5">
          <p className="max-w-prose text-sm text-muted-foreground">
            Your referral link and share picture appear here once your first line is set up. It uses the username on
            that line.
          </p>
          <Button asChild className="mt-4">
            <Link to="/checkout">
              <ApperIcon name="Plus" size={16} />
              Start an order
            </Link>
          </Button>
        </Panel>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
          <Panel title="Share picture" icon="Image" bodyClassName="py-4">
            <ReferralShareCard username={r.username} url={r.url} />
          </Panel>

          <div className="space-y-6">
            <Panel title="Your link" icon="Link2" bodyClassName="py-4">
              <ReferralLinkBox username={r.username} url={r.url} live={r.live} />
            </Panel>

            <div className="grid grid-cols-2 gap-3">
              <Metric
                label="Referrals earned"
                value={me.loading && me.data === undefined ? '…' : earned}
                hint={`Friends on ${MIN_REFERRAL_MONTHS}+ months`}
                icon="Users"
                tone="text-highlight"
              />
              <Metric
                label="Free months"
                value={r.freeMonths}
                hint="On the way to your line"
                icon="Gift"
                tone="text-success"
              />
            </div>

            <Panel title="How it works" icon="Sparkles" bodyClassName="py-2">
              <ol className="divide-y divide-border">
                {STEPS.map((s, i) => (
                  <li key={s.title} className="flex gap-3 py-3">
                    <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-highlight/50 bg-highlight/10 text-sm font-semibold text-highlight tabular-nums">
                      {i + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <ApperIcon name={s.icon} size={16} className="text-primary" />
                        {s.title}
                      </span>
                      <span className="mt-0.5 block text-sm text-muted-foreground">{s.text}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <p className="pb-3 text-xs text-muted-foreground">
                Referrals have to come from a friend. Your own orders and trials don’t count, and the line on your link
                must be active and paid.
              </p>
            </Panel>
          </div>
        </div>
      )}
    </div>
  );
}
